import { format, parseISO } from "date-fns";
import jwtDecode from "jwt-decode";

import { MONTH_NAMES, MONTH_NAMES_SHORT } from "../constants";
import { SPACE_OR_UNDERSCORE_REGEX } from "constants/regexPatterns";

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const getDateInFormat = (dateStr) => {
  if (!dateStr) return "-";
  const date = dateStr?.includes("T") ? new Date(dateStr) : new Date(`${dateStr}T00:00:00Z`);
  const day = date.getUTCDate();
  const month = MONTH_NAMES_SHORT[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month}' ${year}`;
};

// OUTPUT: 15th Aug, 2022
export const getDateFormatForPeru = (dateStr) => {
  if (!dateStr) return "-";

  let date;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split("/");
    date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  } else {
    date = new Date(dateStr);
  }

  if (Number.isNaN(date.getTime())) return "-";

  const day = date.getUTCDate();
  const month = MONTH_NAMES_SHORT[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};

export const getDateInDashForCalendar = (dateStr) => {
  const year = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(dateStr));

  const month = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    timeZone: "UTC",
  }).format(new Date(dateStr));
  const date = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(dateStr));

  return `${year}-${month}-${date}`;
};

export const getDateInDash = (dateStr) => {
  const dateObject = new Date(dateStr);
  const year = dateObject.getFullYear();
  let month = dateObject.getMonth() + 1;
  if (month < 10) {
    month = `0${month}`;
  }
  let date = dateObject.getDate();
  if (date < 10) {
    date = `0${date}`;
  }
  return `${year}-${month}-${date}`;
};

export const getMonthYearFromDate = (dateStr) => {
  if (dateStr) {
    const dateObject = new Date(dateStr);
    return `${MONTH_NAMES_SHORT[dateObject.getMonth()]}’ ${dateObject.getFullYear()}`;
  }
  return "-";
};

export const getFullMonthYearFromDate = (dateStr) => {
  if (dateStr) {
    const dateObject = new Date(dateStr);
    return `${MONTH_NAMES[dateObject.getUTCMonth()]} ${dateObject.getUTCFullYear()}`;
  }
  return "-";
};

export const getFullMontDateYearFromDate = (dateStr) => {
  if (dateStr) {
    return format(new Date(dateStr), "MMM d, yyyy");
  }
  return "_";
};

export const formatToMonthYear = (dateStr) => {
  if (!dateStr) return "-";
  return format(parseISO(dateStr), "MMMM yyyy");
};

export const getGreetingMessageFromDate = () => {
  const msg = new Date().getHours() < 12 ? "Morning" : "Evening";
  return msg;
};

export const formatHeader = (header) => {
  return header.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};

export const getPlaceholderText = (header, selectedOptions) => {
  if (selectedOptions[header]?.length) {
    return null;
  }

  switch (header) {
    case "customers":
      return "Type To Search Customer";
    case "paymentMethodType":
      return "Payment Method Type";
    default:
      return `Select ${formatHeader(header)}`;
  }
};

export const getDecodedToken = (token = null) => {
  if (token) {
    return jwtDecode(token);
  }
  return {};
};

export const getLoggedInUserDetailsFromLocalStorage = () => JSON.parse(localStorage.getItem("loggedInUser")) ?? {};

export const getChipProps = (status) => {
  switch (status) {
    case "Probation":
      return { label: status, color: "warning" };
    case "Permanent":
      return { label: status, color: "success" };
    case "Substitute":
      return { label: status, color: "default" };
    case "Notice_Period":
      return { label: "Notice period", color: "error" };
    default:
      return { label: status };
  }
};

// output "2024-08"
export const formatToYearMonth = (dateStr) => {
  if (!dateStr) return "-";

  const date = new Date(dateStr);

  return format(date, "yyyy-MM");
};

// Monthly -> monthly One-Time -> one-time
export function stringToKebabCase(type) {
  if (typeof type !== "string") return "";

  return type.trim().replace(SPACE_OR_UNDERSCORE_REGEX, "-").toLowerCase();
}
// 1.00 => 1 , 1.5 -> 1.5
export const formatLeaves = (noOfLeaves = "") => {
  const num = parseFloat(noOfLeaves);

  if (num % 1 === 0) {
    return num.toString();
  }

  return num.toString();
};

// ---- Chat helpers ----

// OUTPUT: "JD" (first letter of first and last name, uppercased)
export const getInitials = (firstName, lastName) => {
  return `${(firstName || "")[0] || ""}${(lastName || "")[0] || ""}`.toUpperCase();
};

// Returns the millisecond difference between now and the given date
const getDateDiff = (dateStr) => {
  return new Date() - new Date(dateStr);
};

// OUTPUT: "10:30 AM" | "Yesterday" | "Mar 5"
export const formatChatTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = getDateDiff(dateStr);
  const oneDay = 86400000;

  if (diff < oneDay && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (diff < 2 * oneDay) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

// OUTPUT: "10:30 AM"
export const formatMessageTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// OUTPUT: "Today" | "Yesterday" | "Monday, Mar 5"
export const formatDateHeader = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = getDateDiff(dateStr);
  if (diff < 86400000 && date.getDate() === now.getDate()) return "Today";
  if (diff < 172800000) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

// OUTPUT: "Last seen Mar 5, 10:30 AM"
export const formatLastSeen = (dateStr) => {
  if (!dateStr) return "";
  return `Last seen ${new Date(dateStr).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
};
